import { SiteElement } from '../types';
import { Flag, FlagTriangleRight, Coffee, Paintbrush, Clock, Repeat, Cross, Users, RotateCw, Video, Target, ParkingCircle, DoorOpen, User, TrendingUp, Mountain, AlertTriangle, Home, Mic, Plus, Type } from 'lucide-react';

// Single source of truth for element colors
export const ELEMENT_COLORS: Record<SiteElement['type'], string> = {
  start: '#22c55e',      // green-500
  finish: '#ef4444',     // red-500
  'feed-zone': '#3b82f6',   // blue-500
  'wax-zone': '#a855f7',    // purple-500
  timing: '#eab308',     // yellow-500
  exchange: '#14b8a6',   // teal-500
  medical: '#dc2626',    // red-600
  spectator: '#6366f1',  // indigo-500
  'loop-marker': '#f97316',  // orange-500
  camera: '#06b6d4',     // cyan-500
  'rifle-control': '#f59e0b',  // amber-500
  parking: '#64748b',    // slate-500
  'locker-room': '#10b981',   // emerald-500
  'warmup-zone': '#84cc16',   // lime-500
  tribune: '#f43f5e',    // rose-500
  wc: '#0ea5e9',         // sky-500
  'ski-drop': '#8b5cf6',      // violet-500
  'danger-zone': '#b91c1c',   // red-700
  secretariat: '#3b82f6',     // blue-600
  speaker: '#a855f7',    // purple-600
  custom: '#8b5cf6',     // purple-600
  text: '#000000'        // black
};

// Custom WC component for canvas and toolbar
export const WCIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
    WC
  </div>
);

// Default icons mapping
export const DEFAULT_ELEMENT_ICONS: Record<SiteElement['type'], any> = {
  start: Flag,
  finish: FlagTriangleRight,
  'feed-zone': Coffee,
  'wax-zone': Paintbrush,
  timing: Clock,
  exchange: Repeat,
  medical: Cross,
  spectator: Users,
  'loop-marker': RotateCw,
  camera: Video,
  'rifle-control': Target,
  parking: ParkingCircle,
  'locker-room': DoorOpen,
  'warmup-zone': User,
  tribune: TrendingUp,
  wc: WCIcon,
  'ski-drop': Mountain,
  'danger-zone': AlertTriangle,
  secretariat: Home,
  speaker: Mic,
  custom: Plus,
  text: Type
};

// Element definitions for toolbar
export const ELEMENT_TOOLS = [
  { type: 'start' as const, label: 'Départ' },
  { type: 'finish' as const, label: 'Arrivée' },
  { type: 'feed-zone' as const, label: 'Ravitaillement' },
  { type: 'wax-zone' as const, label: 'Fartage' },
  { type: 'timing' as const, label: 'Chronométrage' },
  { type: 'exchange' as const, label: 'Échange' },
  { type: 'medical' as const, label: 'Médical' },
  { type: 'spectator' as const, label: 'Spectateurs' },
  { type: 'loop-marker' as const, label: 'Boucle' },
  { type: 'camera' as const, label: 'Caméra' },
  { type: 'rifle-control' as const, label: 'Contrôle carabine' },
  { type: 'parking' as const, label: 'Parking' },
  { type: 'locker-room' as const, label: 'Vestiaires' },
  { type: 'warmup-zone' as const, label: 'Échauffement' },
  { type: 'tribune' as const, label: 'Tribune' },
  { type: 'wc' as const, label: 'WC' },
  { type: 'ski-drop' as const, label: 'Dépose de ski' },
  { type: 'secretariat' as const, label: 'Secrétariat' },
  { type: 'speaker' as const, label: 'Speaker' },
  { type: 'danger-zone' as const, label: 'Danger/Exclusion' },
  { type: 'text' as const, label: 'Texte' },
  { type: 'custom' as const, label: 'Personnalisé' }
];

// Helper function to get element color (with optional override)
export function getElementColor(element: SiteElement): string {
  if ((element.type === 'custom' || element.type === 'text') && element.color) {
    return element.color;
  }
  return ELEMENT_COLORS[element.type];
}

// Helper function to hex to RGB conversion for PDF
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : { r: 0, g: 0, b: 0 };
}

// Helper function for PDF export - get RGB color
export function getElementColorForPDF(type: SiteElement['type'], customColor?: string): { r: number; g: number; b: number } {
  if (customColor) {
    return hexToRgb(customColor);
  }
  return hexToRgb(ELEMENT_COLORS[type]);
}
